//
// Copyright (c) 2011-2019 by Curalate, Inc.
//
import { PojoSet } from 'pojo-sets';

import {
  FULLSCREEN_WORKFLOW_ACTIONS,
  FULLSCREEN_WORKFLOW_FLAGS,
  FULLSCREEN_WORKFLOW_NAMES,
  IConditionalFinalToast,
} from '@galleries/models/fullScreenWorkflowModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { IToastNotification } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { ICuralateStore } from '@models/AppState';
import { contentDrawerActions } from '@redux/content-drawer/contentDrawerActions';
import { createAction } from '@utils/actions';
import { ReduxDispatch } from 'app/curalate.store';

const startWorkflow = (workflowName: FULLSCREEN_WORKFLOW_NAMES) => {
  return createAction({
    type: FULLSCREEN_WORKFLOW_ACTIONS.START_WORKFLOW,
    workflowName,
  });
};

const stopWorkflow = (workflowName: FULLSCREEN_WORKFLOW_NAMES) => {
  return createAction({
    type: FULLSCREEN_WORKFLOW_ACTIONS.STOP_WORKFLOW,
    workflowName,
  });
};

const setFlag = (flag: FULLSCREEN_WORKFLOW_FLAGS, val: boolean) => {
  return createAction({
    type: FULLSCREEN_WORKFLOW_ACTIONS.SET_FLAG,
    flag,
    val,
  });
};

const addConditionalFinalToast = (
  toast: IToastNotification,
  requiredFlags: FULLSCREEN_WORKFLOW_FLAGS[],
) => {
  return createAction({
    type: FULLSCREEN_WORKFLOW_ACTIONS.ADD_CONDITIONAL_FINAL_TOAST,
    toast,
    requiredFlags,
  });
};

const resetState = () => {
  return createAction({
    type: FULLSCREEN_WORKFLOW_ACTIONS.RESET_STATE,
  });
};

export type FullScreenWorkflowAction = ReturnType<
  | typeof startWorkflow
  | typeof stopWorkflow
  | typeof addConditionalFinalToast
  | typeof setFlag
  | typeof resetState
>;

const getFinalToast = (
  conditionalFinalToasts: IConditionalFinalToast[],
  flags: PojoSet<FULLSCREEN_WORKFLOW_FLAGS>,
): IToastNotification | undefined => {
  const toastsWithFlags = conditionalFinalToasts.filter((conditionalToast) => {
    return conditionalToast.requiredFlags.every(
      (requiredFlag) => flags[requiredFlag],
    );
  });

  return toastsWithFlags[0] ? toastsWithFlags[0].toast : undefined;
};

export const fullScreenWorkflowActions = {
  setFlag,
  addConditionalFinalToast,

  startWorkflow: (workflowName: FULLSCREEN_WORKFLOW_NAMES) => (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const fullscreenWorkflowMetadata = getState().fullScreenWorkflows
      .fullscreenWorkflowMetadata;
    const isFirstWorkflow =
      Object.keys(fullscreenWorkflowMetadata.inWorkflow).length === 0;

    if (isFirstWorkflow) {
      dispatch(
        contentDrawerActions.setOverrideSettings({
          disableImageUploader: true,
          disableContentDrawer: true,
        }),
      );
    }

    dispatch(startWorkflow(workflowName));
  },

  stopWorkflow: (workflowName: FULLSCREEN_WORKFLOW_NAMES) => (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const fullscreenWorkflowMetadata = getState().fullScreenWorkflows
      .fullscreenWorkflowMetadata;
    const isLastWorkflow =
      Object.keys(fullscreenWorkflowMetadata.inWorkflow).length === 1 &&
      fullscreenWorkflowMetadata.inWorkflow[workflowName];

    if (isLastWorkflow) {
      dispatch(contentDrawerActions.unsetOverrideSettings());

      if (fullscreenWorkflowMetadata.conditionalFinalToasts.length) {
        const currentFlags = fullscreenWorkflowMetadata.flags;
        const finalToast = getFinalToast(
          fullscreenWorkflowMetadata.conditionalFinalToasts,
          currentFlags,
        );

        if (finalToast) {
          dispatch(toastNotificationsActions.addToastNotification(finalToast));
        }
      }

      dispatch(resetState());
    }

    dispatch(stopWorkflow(workflowName));
  },
};
